import {
  Container,
  Mesh,
  MeshGeometry,
  Shader,
  Texture,
  UniformGroup,
} from "pixi.js";

import { OwnedTextureSprite } from "./OwnedTextureSprite";
import {
  PROCEDURAL_RASTER,
  hexToRgb,
  lerp,
  proceduralAlpha,
  proceduralPatternIndex,
  proceduralPixel,
  wrapProceduralTime,
} from "./proceduralPattern";
import {
  PROCEDURAL_GLSL_FRAGMENT,
  PROCEDURAL_GLSL_VERTEX,
  PROCEDURAL_WGSL,
} from "./proceduralShaderSource";

import type { IOverlayElementProcedural } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

import type { OverlayFrameUpdater } from "./OverlayElementAnimator";

interface IProceduralRenderArgs {
  element: IOverlayElementProcedural;
  destination: { width: number; height: number };
}

type ProceduralOptions = IOverlayElementProcedural["options"];

/** Opciones normalizadas como las lee el loop del DOM (defaults incluidos). */
const resolveOptions = (opts: ProceduralOptions) => ({
  pattern: opts.pattern,
  colorA: opts.colorA ?? "#5b21b6",
  colorB: opts.colorB ?? "#06b6d4",
  speed: (opts.speed ?? 50) / 50,
  scale: (opts.scale ?? 50) / 50,
  opacity: Math.max(0, Math.min(1, opts.opacity ?? 1)),
});

type ResolvedOptions = ReturnType<typeof resolveOptions>;

const sameOptions = (a: ResolvedOptions, b: ResolvedOptions): boolean =>
  a.pattern === b.pattern &&
  a.colorA === b.colorA &&
  a.colorB === b.colorB &&
  a.speed === b.speed &&
  a.scale === b.scale &&
  a.opacity === b.opacity;

/** Grupo de uniforms del patrón: mismo orden que `ProceduralUniforms` en WGSL. */
const createProceduralUniforms = () =>
  new UniformGroup({
    uColorA: { value: new Float32Array(3), type: "vec3<f32>" },
    uColorB: { value: new Float32Array(3), type: "vec3<f32>" },
    uTime: { value: 0, type: "f32" },
    uScale: { value: 1, type: "f32" },
    uOpacity: { value: 1, type: "f32" },
    // Como float: el polyfill de pixi.js/unsafe-eval escribe los i32 como float.
    uPattern: { value: 0, type: "f32" },
  });

type ProceduralUniforms = ReturnType<typeof createProceduralUniforms>;

const writeRgb01 = (out: Float32Array, hex: string) => {
  const [r, g, b] = hexToRgb(hex);
  out[0] = r / 255;
  out[1] = g / 255;
  out[2] = b / 255;
};

/** Vuelca colores/escala/opacidad/patrón al grupo (todo menos `uTime`). */
const applyOptionsToUniforms = (
  uniforms: ProceduralUniforms,
  o: ResolvedOptions,
) => {
  const u = uniforms.uniforms;
  writeRgb01(u.uColorA, o.colorA);
  writeRgb01(u.uColorB, o.colorB);
  u.uScale = o.scale;
  u.uOpacity = o.opacity;
  u.uPattern = proceduralPatternIndex(o.pattern);
};

/**
 * Mesh dueño de su geometría y su shader (uniforms propios del elemento): los
 * destruye con él por cualquier camino (factories con `children: true`,
 * teardown genérico, huérfano) para no dejar buffers ni bind groups vivos en
 * la GPU. Los programas GLSL/WGSL quedan en la caché de Pixi (compartidos por
 * todos los procedurales: `Shader.destroy()` sin `destroyPrograms`).
 */
class OwnedShaderMesh extends Mesh<MeshGeometry, Shader> {
  override destroy(options?: Parameters<Mesh["destroy"]>[0]): void {
    const { geometry, shader } = this;
    super.destroy(options);
    geometry.destroy(true);
    shader?.destroy();
  }
}

let warnedShaderFallback = false;

/**
 * Pixi equivalente de `OverlayElementProcedural` (DOM). Dibuja el patrón con
 * un fragment shader (GLSL para WebGL2, WGSL para WebGPU;
 * `proceduralShaderSource.ts`) sobre un quad a los bounds del elemento: la
 * matemática es la del loop CPU (`proceduralPattern.ts`) y el shader emula el
 * raster 192×108 estirado, así el look es el mismo pero sin trabajo por frame
 * en CPU ni subida de textura: el updater solo mueve `uTime` (y re-vuelca las
 * opciones si cambiaron). Si no se puede crear el shader cae al canvas 2D
 * de siempre (`renderCanvasFallback`) avisando una vez por consola.
 */
export class OverlayProceduralRenderer {
  render(
    container: Container,
    args: IProceduralRenderArgs,
  ): OverlayFrameUpdater | null {
    const { element, destination } = args;

    const wrapper = new Container();
    if (element.rotation) {
      const rad = (element.rotation * Math.PI) / 180;
      const pivotX = destination.width / 2;
      const pivotY = destination.height / 2;
      wrapper.pivot.set(pivotX, pivotY);
      wrapper.position.set(
        element.bounds.left + pivotX,
        element.bounds.top + pivotY,
      );
      wrapper.rotation = rad;
    } else {
      wrapper.position.set(element.bounds.left, element.bounds.top);
    }
    container.addChild(wrapper);

    if (destination.width <= 0 || destination.height <= 0) return null;

    // Recursos propios del elemento (mesh/shader o textura): se destruyen
    // junto con el wrapper. Huérfano tras un removeChildren() sin destroy
    // (teardown genérico): se destruye solo si nadie lo re-parenta en el
    // mismo tick.
    wrapper.on("removed", () => {
      queueMicrotask(() => {
        if (!wrapper.destroyed && !wrapper.parent) {
          wrapper.destroy({ children: true });
        }
      });
    });

    try {
      return renderShader(wrapper, element, destination);
    } catch (err) {
      if (!warnedShaderFallback) {
        warnedShaderFallback = true;
        console.warn(
          "[OverlayProceduralRenderer] shader procedural no disponible; usando canvas 2D",
          err,
        );
      }
      return renderCanvasFallback(wrapper, element, destination);
    }
  }
}

/** Path GPU: quad + shader por elemento; lanza si Pixi no puede crearlos. */
const renderShader = (
  wrapper: Container,
  element: IOverlayElementProcedural,
  destination: { width: number; height: number },
): OverlayFrameUpdater => {
  const w = destination.width;
  const h = destination.height;
  const uniforms = createProceduralUniforms();
  let current = resolveOptions(element.options);
  applyOptionsToUniforms(uniforms, current);

  const shader = Shader.from({
    gl: {
      vertex: PROCEDURAL_GLSL_VERTEX,
      fragment: PROCEDURAL_GLSL_FRAGMENT,
      name: "overlay-procedural",
    },
    gpu: {
      vertex: { source: PROCEDURAL_WGSL, entryPoint: "mainVert" },
      fragment: { source: PROCEDURAL_WGSL, entryPoint: "mainFrag" },
      name: "overlay-procedural",
    },
    resources: { proceduralUniforms: uniforms },
  });

  // Quad a los bounds; UV [0,1] = (x/W, y/H) del raster emulado.
  const geometry = new MeshGeometry({
    positions: new Float32Array([0, 0, w, 0, w, h, 0, h]),
    uvs: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
    indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
  });
  const mesh = new OwnedShaderMesh({ geometry, shader });
  wrapper.addChild(mesh);

  return (elapsedMs: number) => {
    if (wrapper.destroyed) return true;
    const next = resolveOptions(element.options);
    if (!sameOptions(next, current)) {
      current = next;
      applyOptionsToUniforms(uniforms, current);
    }
    const time = (elapsedMs / 1000) * current.speed;
    uniforms.uniforms.uTime = wrapProceduralTime(
      current.pattern,
      time,
      current.scale,
    );
    uniforms.update();
    return false;
  };
};

/**
 * Path CPU de respaldo (el original): loop por píxel sobre un canvas 192×108
 * subido como textura cada frame; el Sprite estira el raster a los bounds.
 */
const renderCanvasFallback = (
  wrapper: Container,
  element: IOverlayElementProcedural,
  destination: { width: number; height: number },
): OverlayFrameUpdater | null => {
  const W = PROCEDURAL_RASTER.width;
  const H = PROCEDURAL_RASTER.height;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const img = ctx.createImageData(W, H);
  const data = img.data;

  const texture = Texture.from(canvas);
  // Sprite dueño de la textura: la destruye con él (cualquier teardown).
  const sprite = new OwnedTextureSprite(texture);
  sprite.width = destination.width;
  sprite.height = destination.height;
  wrapper.addChild(sprite);

  return (elapsedMs: number) => {
    if (wrapper.destroyed) return true;
    const o = resolveOptions(element.options);
    const a = hexToRgb(o.colorA);
    const b = hexToRgb(o.colorB);
    const time = (elapsedMs / 1000) * o.speed;

    for (let y = 0; y < H; y += 1) {
      for (let x = 0; x < W; x += 1) {
        const v = proceduralPixel(o.pattern, x / W, y / H, time, o.scale);
        const i = (y * W + x) * 4;
        data[i] = lerp(a[0], b[0], v);
        data[i + 1] = lerp(a[1], b[1], v);
        data[i + 2] = lerp(a[2], b[2], v);
        data[i + 3] = Math.round(255 * proceduralAlpha(o.pattern, v, o.opacity));
      }
    }
    ctx.putImageData(img, 0, 0);
    texture.source.update();
  };
};

export default OverlayProceduralRenderer;
